import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getSubmission, updateSubmission, reviewSubmission, clearCurrentSubmission } from '../actions/submissionActions';
import SubmissionForm from '../components/content-management/SubmissionForm';
import ContentManagementSidebar from '../components/content-management/ContentManagementSidebar';
import { isAdminRole } from '../utils/roleHelpers';

const SubmissionEditor = ({
  auth: { user },
  submission: { currentSubmission, loading: submissionLoading },
  getSubmission,
  updateSubmission,
  reviewSubmission,
  clearCurrentSubmission
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showForm, setShowForm] = useState(false);

  // Determine mode from URL path
  const isView = location.pathname.includes('/view');
  const isEdit = location.pathname.includes('/edit');
  const mode = isView ? 'view' : 'edit';

  // Role detection
  const isAdmin = isAdminRole(user?.role);
  const isInstructor = user?.role === 'instructor';

  useEffect(() => {
    // Load submission data if viewing or editing
    if (id) {
      getSubmission(id);
    }

    // Show form after short delay to ensure data is loaded
    const timer = setTimeout(() => {
      setShowForm(true);
    }, 100);

    // Cleanup: clear current submission when component unmounts
    return () => {
      clearTimeout(timer);
      clearCurrentSubmission();
    };
  }, [id, getSubmission, clearCurrentSubmission]);

  const handleClose = () => {
    navigate('/content-management/submissions');
  };

  const handleSubmit = async (submissionData) => {
    try {
      if (isEdit && id) {
        // Use reviewSubmission for status updates, updateSubmission for other changes
        if (submissionData.status && submissionData.status !== currentSubmission?.status) {
          await reviewSubmission(id, submissionData);
        } else {
          await updateSubmission(id, submissionData);
        }
      }
      // Navigate back to submissions list after successful save
      navigate('/content-management/submissions');
    } catch (error) {
      console.error('Error submitting submission:', error);
      throw error;
    }
  };

  const handleTabChange = (tabId) => {
    if (tabId === 'Submissions') {
      navigate('/content-management/submissions');
    } else {
      navigate('/content-management', { state: { activeTab: tabId } });
    }
  };

  // Show loading spinner while data is being fetched
  if (submissionLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
        <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
          <ContentManagementSidebar
            activeTab="Submissions"
            onTabChange={handleTabChange}
            isAdmin={isAdmin}
            isInstructor={isInstructor}
          />
          <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 pb-6 px-6 min-w-0">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1086a4]"></div>
              <span className="ml-4 text-gray-600">Loading submission...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show form only after data is ready
  if (!showForm) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
        <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
          <ContentManagementSidebar
            activeTab="Submissions"
            onTabChange={handleTabChange}
            isAdmin={isAdmin}
            isInstructor={isInstructor}
          />
          <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 pb-6 px-6 min-w-0">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1086a4]"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
      <div className="w-full bg-white rounded-lg shadow-sm flex flex-col md:flex-row">
        <ContentManagementSidebar
          activeTab="Submissions"
          onTabChange={handleTabChange}
          isAdmin={isAdmin}
          isInstructor={isInstructor}
        />
        <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 min-w-0">
          <SubmissionForm
            mode={mode}
            submission={currentSubmission}
            onClose={handleClose}
            onSubmit={handleSubmit}
            inline={true}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  submission: state.submission
});

export default connect(
  mapStateToProps,
  {
    getSubmission,
    updateSubmission,
    reviewSubmission,
    clearCurrentSubmission
  }
)(SubmissionEditor);
